import type { Model } from '../../types';

/**
 * A Model Config's own choices and the caps they must respect (tasks 1074,
 * 1167). Moved out of the retired full-page config form so the config dialog
 * on the Models page applies the same rules: an output limit no larger than the
 * Model's `maxOutputTokens`, a context budget no larger than its
 * `contextWindow` (blank = the whole window), and a temperature from 0 to 2.
 * innovate-service enforces both caps too; checking here keeps the User from
 * submitting a form the service will refuse.
 */

export interface ModelConfigFormData {
  name: string;
  modelId: string;
  maxOutputTokens: number | '';
  temperature: number | '';
  contextBudgetTokens: number | '';
  promptCaching: boolean;
}

export const modelLabel = (model: Pick<Model, 'displayName' | 'modelId'>) => model.displayName ?? model.modelId;

/** The caps a config's numbers must respect, for the selected Model. */
export function validateAgainstModel(
  values: ModelConfigFormData,
  model: Model | undefined,
  unavailable?: string,
): Partial<Record<keyof ModelConfigFormData, string>> {
  const errors: Partial<Record<keyof ModelConfigFormData, string>> = {};

  if (!values.modelId) {
    errors.modelId = 'Model is required';
  } else if (!model && unavailable) {
    // A Model id the selector does not offer (retired, or not in the catalog).
    errors.modelId = unavailable;
  }

  if (values.maxOutputTokens === '' || Number(values.maxOutputTokens) <= 0) {
    errors.maxOutputTokens = 'Max output tokens must be greater than 0';
  } else if (model && Number(values.maxOutputTokens) > model.maxOutputTokens) {
    errors.maxOutputTokens = `Must be at most ${model.maxOutputTokens.toLocaleString('en-US')}, the output limit of ${modelLabel(model)}`;
  }

  if (values.temperature === '' || Number(values.temperature) < 0 || Number(values.temperature) > 2) {
    errors.temperature = 'Temperature must be between 0 and 2';
  }

  if (values.contextBudgetTokens !== '') {
    if (Number(values.contextBudgetTokens) <= 0) {
      errors.contextBudgetTokens = 'Context budget must be greater than 0, or blank to use the full window';
    } else if (model && Number(values.contextBudgetTokens) > model.contextWindow) {
      errors.contextBudgetTokens = `Must be at most ${model.contextWindow.toLocaleString('en-US')}, the context window of ${modelLabel(model)}`;
    }
  }

  return errors;
}
